import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { CheckCircle, ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { passwordSetupService, PasswordSetupState } from '../services/passwordSetupService';
import { redirectToLogin } from '../services/httpClient';

// Página PÚBLICA de definição de senha (RN-005b): aberta pelo link do email, sem sessão.
// Serve ao convite de primeiro acesso e ao "esqueci minha senha" — o purpose muda só o texto.

const PASSWORD_MIN = 8;
const PASSWORD_MAX = 100;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    '& fieldset': { borderColor: '#ced4da', legend: { maxWidth: '100%' } },
    '&:hover fieldset': { borderColor: '#ced4da' },
    '&.Mui-focused fieldset': {
      borderColor: '#03B4C6',
      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    color: '#6c757d',
    backgroundColor: 'white',
    paddingLeft: '4px',
    paddingRight: '4px',
    '&.Mui-focused': { color: '#03B4C6' },
  },
};

const decodeShortCode = (code: string | null): { alias: string; token: string } | null => {
  if (!code) return null;
  try {
    const base64 = code.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const plain = decodeURIComponent(escape(atob(padded)));
    const sep = plain.indexOf(':');
    if (sep <= 0 || sep === plain.length - 1) return null;
    return { alias: plain.slice(0, sep), token: plain.slice(sep + 1) };
  } catch {
    return null;
  }
};

const SetPassword: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  // `c` é o código curto do botão do WhatsApp (RN-005d): base64url de "alias:token", porque o
  // template da Meta aceita UMA variável no fim da URL. O link do email segue com clinic+token.
  const decoded = decodeShortCode(params.get('c'));
  const alias = decoded?.alias ?? params.get('clinic') ?? '';
  const token = decoded?.token ?? params.get('token') ?? '';

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<PasswordSetupState | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!alias || !token) {
      setState({ status: 'invalid', clinicName: null, purpose: '' });
      setLoading(false);
      return;
    }
    passwordSetupService.getState(alias, token)
      .then(setState)
      .catch(() => setState({ status: 'invalid', clinicName: null, purpose: '' }))
      .finally(() => setLoading(false));
  }, [alias, token]);

  const isInvite = state?.purpose === 'invite';

  const handleSubmit = async () => {
    if (password.length < PASSWORD_MIN) {
      setError(`A senha deve ter no mínimo ${PASSWORD_MIN} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('As senhas não conferem.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await passwordSetupService.setPassword(alias, token, password);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'Users.PasswordLinkInvalid') {
        // O link expirou/foi usado entre abrir a página e enviar — reflete o novo estado.
        setState((prev) => prev ? { ...prev, status: 'expired' } : prev);
      } else {
        setError(err?.response?.data?.description || 'Não foi possível definir a senha. Tente novamente.');
      }
    } finally {
      setSaving(false);
    }
  };

  const clinicName = state?.clinicName || 'Clinic4Us';

  const message = (icon: React.ReactNode, title: string, text: string, showLoginButton = false) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: '#6c757d', mt: 1, mb: showLoginButton ? 3 : 0 }}>{text}</Typography>
      {showLoginButton && (
        <Button
          onClick={() => redirectToLogin()}
          variant="contained"
          sx={{
            backgroundColor: '#03B4C6', color: 'white', textTransform: 'none', fontWeight: 600,
            padding: '0.6rem 2rem', '&:hover': { backgroundColor: '#029AAB' },
          }}
        >
          Ir para o login
        </Button>
      )}
    </Box>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#03B4C6' }} />
        </Box>
      );
    }

    if (done) {
      return message(
        <CheckCircle sx={{ fontSize: '3rem', color: '#28a745' }} />,
        'Senha definida!',
        'Sua senha foi criada com sucesso. Use-a para entrar no sistema.',
        true);
    }

    if (state?.status !== 'valid') {
      const texts: Record<string, string> = {
        expired: 'Este link expirou. Peça um novo pela opção "Esqueci a senha" na tela de login.',
        used: 'Este link já foi utilizado. Se precisar, defina uma nova senha pela opção "Esqueci a senha".',
        revoked: 'Este link não é mais válido. Peça um novo pela opção "Esqueci a senha".',
        invalid: 'Link inválido. Verifique se copiou o endereço completo do email.',
      };
      return message(
        <ErrorOutline sx={{ fontSize: '3rem', color: '#dc3545' }} />,
        'Link indisponível',
        texts[state?.status || 'invalid'] || texts.invalid,
        true);
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: '#6c757d' }}>
          {isInvite
            ? 'Bem-vindo! Crie sua senha de acesso para começar a usar o sistema.'
            : 'Crie uma nova senha para a sua conta.'}
        </Typography>

        <TextField
          label="Nova senha"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(''); }}
          size="small"
          fullWidth
          autoComplete="new-password"
          inputProps={{ maxLength: PASSWORD_MAX }}
          InputLabelProps={{ shrink: true }}
          helperText={`Mínimo de ${PASSWORD_MIN} caracteres`}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((v) => !v)}
                  edge="end"
                  size="small"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={fieldSx}
        />

        <TextField
          label="Confirme a senha"
          type={showPassword ? 'text' : 'password'}
          value={confirm}
          onChange={(e) => { setConfirm(e.target.value); setError(''); }}
          size="small"
          fullWidth
          autoComplete="new-password"
          inputProps={{ maxLength: PASSWORD_MAX }}
          InputLabelProps={{ shrink: true }}
          error={confirm.length > 0 && confirm !== password}
          helperText={confirm.length > 0 && confirm !== password ? 'As senhas não conferem' : ' '}
          sx={fieldSx}
        />

        {error && (
          <Typography variant="body2" sx={{ color: '#dc3545' }}>{error}</Typography>
        )}

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          fullWidth
          sx={{
            backgroundColor: '#03B4C6', color: 'white', textTransform: 'none', fontWeight: 600,
            padding: '0.75rem', fontSize: '1rem', mt: 1,
            '&:hover': { backgroundColor: '#029AAB' },
          }}
        >
          {saving ? 'Salvando...' : (isInvite ? 'Criar senha' : 'Redefinir senha')}
        </Button>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f8', py: { xs: 0, sm: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 460,
          margin: '0 auto',
          borderRadius: { xs: 0, sm: '12px' },
          overflow: 'hidden',
          border: '1px solid #e9ecef',
        }}
      >
        <Box sx={{ padding: { xs: '1.5rem', sm: '2rem' } }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#495057', fontWeight: 600 }}>
              {clinicName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              {isInvite ? 'Primeiro acesso' : 'Redefinição de senha'}
            </Typography>
          </Box>
          {renderBody()}
        </Box>
      </Paper>
    </Box>
  );
};

export default SetPassword;
